// src/utils/rrLive/rrResultTables.js

/**
 * Host-side RR tables/matrix utils.
 *
 * We only persist rgb_smooth_signal in Storage/Firestore, not peaks/troughs.
 * So we re-detect troughs from the smooth signal for display purposes.
 */

/**
 * @typedef {object} SmoothPoint
 * @property {number} t
 *   Time position for the point, in seconds.
 * @property {number} v
 *   Smoothed signal value at time `t`.
 */

/**
 * Extract numeric value from smooth point with a tolerant accessor.
 * Supports {v}, {y}, {value}.
 */
function readV(p) {
  const v = p?.v ?? p?.y ?? p?.value ?? null;
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function readT(p) {
  const t = p?.t ?? null;
  return typeof t === "number" && Number.isFinite(t) ? t : null;
}

function safeNum(x) {
  return Number.isFinite(x) ? x : null;
}

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

/**
 * Detect trough indices from a smooth signal.
 *
 * Heuristic:
 * - local minima: v[i] < v[i-1] and v[i] <= v[i+1]
 * - enforce min separation in time (sec) to avoid picking noise
 * - optionally require a minimum "prominence" vs surrounding neighborhood
 */
export function detectTroughsFromSmoothSignal(
  smoothSignal,
  {
    minSepSec = 0.8, // prevent dense duplicates
    neighborhood = 3, // compare within +/- N points to estimate prominence
    minProminence = 0.0, // can tune if needed (0 keeps permissive)
  } = {},
) {
  if (!Array.isArray(smoothSignal) || smoothSignal.length < 5) return [];

  const candidates = [];

  for (let i = 1; i < smoothSignal.length - 1; i += 1) {
    const v0 = readV(smoothSignal[i - 1]);
    const v1 = readV(smoothSignal[i]);
    const v2 = readV(smoothSignal[i + 1]);
    const t1 = readT(smoothSignal[i]);

    if (v0 == null || v1 == null || v2 == null || t1 == null) continue;

    // Local minimum
    if (!(v1 < v0 && v1 <= v2)) continue;

    // Prominence check (optional)
    if (minProminence > 0) {
      const left = [];
      const right = [];
      for (let k = 1; k <= neighborhood; k += 1) {
        const lv = readV(smoothSignal[i - k]);
        const rv = readV(smoothSignal[i + k]);
        if (lv != null) left.push(lv);
        if (rv != null) right.push(rv);
      }
      const lMax = left.length ? Math.max(...left) : null;
      const rMax = right.length ? Math.max(...right) : null;
      const aroundMax =
        lMax != null && rMax != null ? Math.max(lMax, rMax) : (lMax ?? rMax);

      if (aroundMax != null) {
        const prom = aroundMax - v1;
        if (!(prom >= minProminence)) continue;
      }
    }

    candidates.push({ index: i, t: t1 });
  }

  if (!candidates.length) return [];

  // Enforce min separation by time
  candidates.sort((a, b) => a.t - b.t);

  const chosen = [];
  let lastT = -Infinity;
  for (const c of candidates) {
    if (c.t - lastT < minSepSec) continue;
    chosen.push({ index: c.index });
    lastT = c.t;
  }

  return chosen;
}

/**
 * Convert trough indices to trough times in seconds.
 */
export function sortedTroughTimesSec(smoothSignal, troughs) {
  if (!Array.isArray(troughs) || troughs.length < 2) return [];
  if (!Array.isArray(smoothSignal) || smoothSignal.length === 0) return [];

  const sorted = [...troughs].sort((a, b) => a.index - b.index);

  const out = [];
  for (const tr of sorted) {
    const t = readT(smoothSignal[tr.index]);
    if (t != null) out.push(t);
  }
  return out;
}

/**
 * Build per-breath RR rows from trough times.
 * (Breath i is between trough i and trough i+1)
 */
export function buildRrPerBreathRows(
  timesSec,
  { minGapSec = 0.5, maxSec = 10 } = {},
) {
  const rows = [];
  if (!Array.isArray(timesSec) || timesSec.length < 2) return rows;

  for (let i = 0; i < timesSec.length - 1; i += 1) {
    const dur = timesSec[i + 1] - timesSec[i];
    if (!(dur > minGapSec && dur < maxSec)) continue;

    rows.push({
      breath: i + 1,
      durationSec: safeNum(dur),
      rr: safeNum(60 / dur),
    });
  }
  return rows;
}

/**
 * Build the RR range comparison matrix (upper triangle),
 * plus diagonal as single-breath RR.
 */
export function buildRrComparisonMatrix(
  timesSec,
  { minGapSec = 0.5, diagMaxSec = 10, rangeMaxSec = 60 } = {},
) {
  if (!Array.isArray(timesSec) || timesSec.length < 2) {
    return { n: 0, matrix: [], valid: false };
  }

  const n = timesSec.length;
  const matrix = Array.from({ length: n }, () =>
    Array.from({ length: n }, () => null),
  );

  // diagonal: single breath
  for (let i = 0; i < n - 1; i += 1) {
    const dur = timesSec[i + 1] - timesSec[i];
    if (!(dur > minGapSec && dur < diagMaxSec)) continue;
    matrix[i][i] = safeNum(60 / dur);
  }

  // upper triangle: range
  for (let s = 0; s < n - 1; s += 1) {
    for (let e = s + 1; e < n; e += 1) {
      const dur = timesSec[e] - timesSec[s];
      const breaths = e - s;
      if (!(dur > minGapSec && dur < rangeMaxSec)) continue;
      matrix[s][e] = safeNum((breaths / dur) * 60);
    }
  }

  return { n, matrix, valid: true };
}

/**
 * Heat scale: use p95 of |v-overall| to avoid outliers dominating.
 */
export function computeHeatScaleP95(
  { rrMatrix, overallRR },
  { minDelta = 0.25, pctl = 0.95 } = {},
) {
  if (!rrMatrix?.valid || rrMatrix.n < 2) return { maxDelta: 1 };
  if (!Number.isFinite(overallRR) || overallRR <= 0) return { maxDelta: 1 };

  const diffs = [];
  for (let i = 0; i < rrMatrix.n; i += 1) {
    for (let j = i; j < rrMatrix.n; j += 1) {
      const v = rrMatrix.matrix[i]?.[j];
      if (!Number.isFinite(v)) continue;
      diffs.push(Math.abs(v - overallRR));
    }
  }
  if (!diffs.length) return { maxDelta: 1 };

  diffs.sort((a, b) => a - b);
  const idx = Math.floor(pctl * (diffs.length - 1));
  const p = diffs[idx] ?? diffs[diffs.length - 1] ?? 1;

  return { maxDelta: Math.max(minDelta, Number.isFinite(p) ? p : 1) };
}

/**
 * Create a "heat style" callback (same behaviour as pod).
 * NOTE: rgba colour is hard-coded to your existing cyan-ish tone.
 */
export function makeHeatStyle({ overallRR, maxDelta }) {
  const clamp01 = (x) => Math.max(0, Math.min(1, x));

  // Map delta ratio -> color (green -> yellow -> red)
  // t = 0   => green
  // t ~0.5  => yellow/orange
  // t = 1   => red
  const colorFor = (t) => {
    // We'll use HSL hue sweep: 120 (green) -> 0 (red)
    // Keep saturation fairly high; lightness adjusted a bit for readability.
    const hue = 120 - 120 * t; // 120..0
    const sat = 85; // %
    const light = 42; // %
    return `hsl(${hue} ${sat}% ${light}%)`;
  };

  return (v) => {
    if (!overallRR || v == null || !Number.isFinite(v)) return undefined;

    const delta = Math.abs(v - overallRR);
    const t = clamp01(delta / (maxDelta || 1)); // 0..1 (0 best)

    // Opacity: keep strong when close (green), fade as it gets worse
    const minA = 0.18;
    const maxA = 0.92;
    const a = maxA - t * (maxA - minA);

    const bg = colorFor(t);
    const backgroundColor = bg; // solid color
    const opacity = a;

    // Text colour: choose based on opacity (and thus perceived darkness)
    const fg =
      opacity < 0.35
        ? "var(--greyscale-2, #6b7280)"
        : "var(--greyscale-1, #ffffff)";

    return { backgroundColor, opacity, color: fg };
  };
}
